import { DateTimeFactory } from "../../../../../modules/localization/useDateTime"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { ExtraServiceActionType } from "../../../../../modules/merchant/admin/extraServices/extraServicesTypes"
import {
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    CreateOrderDialogType,
    CreateOrderFlowType,
    OrderLineType,
} from "../../createOrderTypes"
import { getCreateOrderDialogTypeFlags } from "../../hooks/useCreateOrderDialogType"
import { DeliveryOrderLine, DeliveryTimesField, DynamicConfigKey, ProductWishOrderLine } from "./formTypes"

type MultipleDeliveriesState = {
    mode?: CreateOrderDialogMode
    dialogType: CreateOrderDialogType
    flowConfig?: CreateOrderDialogFlow
    orderDetails: MeetingServiceOrder
    dateTimeFactory: DateTimeFactory
}

export function buildMultipleDeliveriesState({
    mode,
    dialogType,
    flowConfig,
    orderDetails,
    dateTimeFactory,
}: MultipleDeliveriesState): DeliveryTimesField | null {
    const isOutlookFlow = flowConfig?.flow === CreateOrderFlowType.OUTLOOK
    if (mode === CreateOrderDialogMode.CREATE && !isOutlookFlow) return null
    const { isSupplementaryOrder, isRegularOrder } = getCreateOrderDialogTypeFlags(dialogType)

    const meetingStartIso = orderDetails.meetingDetails?.startDateTime
    const deliveries = orderDetails.deliveries ?? []
    const useMeetingStart = isOutlookFlow && !!meetingStartIso && !deliveries.length

    const meetingStart = meetingStartIso ? dateTimeFactory.fromApi(meetingStartIso) : null
    const hasBookedResources = !!orderDetails.bookingDetails?.resources?.length

    const baseDateTime = useMeetingStart
        ? meetingStart
        : deliveries.length
          ? dateTimeFactory.fromApi(deliveries[0].deliveryTime)
          : null

    // The booking owns the date: when it was moved after the deliveries were created,
    // re-anchor the delivery date to the booking so catering follows the meeting.
    const syncedMeetingDate =
        mode === CreateOrderDialogMode.EDIT &&
        isRegularOrder &&
        hasBookedResources &&
        meetingStart &&
        baseDateTime &&
        !meetingStart.hasSame(baseDateTime, "day")
            ? meetingStart
            : null

    const activeTime = baseDateTime ? dateTimeFactory.toHourMinute(baseDateTime) : null
    const deliveryDate =
        mode === CreateOrderDialogMode.DUPLICATE
            ? null
            : ((syncedMeetingDate ?? baseDateTime)?.toFormat("yyyy-LL-dd") ?? null)

    const deliveryTimes = useMeetingStart
        ? [
              {
                  time: activeTime,
                  orderLines: [], // No order lines from meetingDetails
              },
          ]
        : deliveries.map((d) => {
              const time = dateTimeFactory.toHourMinute(dateTimeFactory.fromApi(d.deliveryTime))
              const orderLines: DeliveryOrderLine[] = isSupplementaryOrder
                  ? []
                  : d.orderLines.map((l): DeliveryOrderLine => {
                        if (l.type === OrderLineType.EXTRA_SERVICE) {
                            const isAutoAdded =
                                l.action === ExtraServiceActionType.ADD_ALWAYS ||
                                l.action === ExtraServiceActionType.ADD_WHEN_PICK_UP ||
                                l.action === ExtraServiceActionType.ADD_WHEN_DELIVERED
                            return {
                                id: l.productId,
                                name: l.name,
                                marketingDetails: undefined,
                                quantity: l.items,
                                isActive: true,
                                price: l.price,
                                isExtraService: true,
                                ...(l.action ? { action: l.action } : {}),
                                ...(isAutoAdded ? { isAutoAddedExtraService: true } : {}),
                            }
                        }
                        if (l.type === DynamicConfigKey.PRODUCT_WISH) {
                            const wishLine: ProductWishOrderLine = {
                                type: DynamicConfigKey.PRODUCT_WISH,
                                id: `${time}_${l.name}`,
                                name: l.name,
                                quantity: l.items,
                                price: l.itemPrice,
                            }
                            return wishLine
                        }
                        return {
                            id: l.productId,
                            name: l.name,
                            marketingDetails: undefined,
                            quantity: l.items,
                            isActive: true,
                            price: l.itemPrice,
                            isExtraService: false,
                            priceListId: l.priceListId,
                        }
                    })
              return { time, orderLines }
          })

    return { activeTime, deliveryDate, deliveryTimes }
}
